/**
 * Overview share modal + ACL grants table (FE-5c) — extracted from overview.route.js.
 */

import { granteesFullyCoveringDevices } from "./overview-group-slot-helpers.js";
import {
  formatGroupShareApplyError,
  isGroupShareDbBusyError,
} from "./group-share-busy-ux.js";
import {
  sortOverviewShareGrantRows,
  buildOverviewShareGrantsStatLine,
  buildOverviewShareGrantsTableHtml,
} from "./overview-share-grants-render.js";
import {
  buildShareModalDevicePickerRowsHtml,
  loadShareModalDevicePickerPage,
  mergeSharePickerDevicePages,
  shareModalDevicePickerTruncatedHtml,
} from "./share-modal-device-picker.js";

/**
 * @param {object} ctx
 * @returns {{
 *   loadShareGrants: () => Promise<void>,
 *   refreshShareItemsSilently: () => Promise<void>,
 *   openShareModal: (prefillGroup?: string, prefillOwner?: string, editSpec?: object | null) => Promise<void>,
 *   closeShareModal: () => void,
 *   wireHandlers: () => void,
 * }}
 */
export function createOverviewShareChrome(ctx) {
  const {
    view,
    routeSeq,
    shareModalEl,
    api,
    getMe,
    toast,
    escapeHtml,
    $,
    $$,
    setChildMarkup,
    isRouteCurrent,
    getDevices,
    inferUniqueOwnerForGroup,
    groupDeviceRow,
    canonicalGroupKey,
    buildAuthUsersPath,
    ADMIN_CONSOLE_TIMEOUT_MS,
    buildAdminNotificationGroupSharePreviewPath,
    HOME_CONSOLE_TIMEOUT_MS,
    buildAdminDeviceShareRevokePath,
    fetchGroupShareGrantees,
    granteePermissionSummary,
    syncGroupShareGrantees,
    fetchAllAdminShares,
    grantShareMatrix,
    bustDeviceListCaches,
    canManageShares,
  } = ctx;

  const st = {
    sharePrefillGroup: "",
    sharePrefillOwner: "",
    shareModalUsersCache: [],
    shareModalEditSpec: null,
    shareModalGroupMode: false,
    shareModalGroupInitialGrantees: null,
    shareModalGroupGranteesLoadOk: true,
    overviewShareItems: [],
    shareDevChangeBound: false,
    sharePickerDevices: [],
    sharePickerNextCursor: null,
    sharePickerHasMore: false,
    sharePickerUsingCacheFallback: false,
  };

  function ensureShareDeviceLoadMoreUi() {
    const listEl = $("#shareDeviceList", view);
    if (!listEl || listEl.parentNode.querySelector("#shareDeviceLoadMoreWrap")) return;
    const wrap = document.createElement("div");
    wrap.id = "shareDeviceLoadMoreWrap";
    wrap.style.cssText = "margin-top:8px;display:none";
    wrap.innerHTML = `<button type="button" class="btn sm secondary btn-tap" id="shareDeviceLoadMore">Load more boards</button>`;
    listEl.parentNode.insertBefore(wrap, listEl.nextSibling);
  }

  function readShareDevicePickerChecked() {
    const preserve = new Map();
    $$("#shareDeviceList input[type='checkbox']", view).forEach((inp) => {
      const v = String(inp.value || "").trim();
      if (!inp.disabled && v) preserve.set(v, !!inp.checked);
    });
    return preserve;
  }

  function renderShareDevicePicker(preserveChecks = true) {
    const devListEl = $("#shareDeviceList", view);
    const loadWrap = $("#shareDeviceLoadMoreWrap", view);
    if (!devListEl) return;
    const preserve = preserveChecks ? readShareDevicePickerChecked() : new Map();
    const picked = new Set([...preserve.entries()].filter(([, c]) => c).map(([id]) => id));
    const ownCount = st.sharePickerDevices.filter((d) => d && !d.is_shared).length;
    let banner = shareModalDevicePickerTruncatedHtml(ownCount, st.sharePickerHasMore);
    if (st.sharePickerUsingCacheFallback) {
      banner = `<p class="badge offline" style="margin:0 0 8px">Board list load failed — using the Home list (may be incomplete).</p>${banner}`;
    }
    setChildMarkup(
      devListEl,
      banner + buildShareModalDevicePickerRowsHtml(st.sharePickerDevices, picked, groupDeviceRow),
    );
    if (loadWrap) loadWrap.style.display = (!st.shareModalGroupMode && !st.shareModalEditSpec && st.sharePickerHasMore) ? "" : "none";
  }

  async function loadShareDevicePickerInitial() {
    st.sharePickerDevices = [];
    st.sharePickerNextCursor = null;
    st.sharePickerHasMore = false;
    st.sharePickerUsingCacheFallback = false;
    ensureShareDeviceLoadMoreUi();
    const devListEl = $("#shareDeviceList", view);
    if (devListEl) setChildMarkup(devListEl, `<p class="muted">Loading boards…</p>`);
    try {
      const pg = await loadShareModalDevicePickerPage({
        live: false,
        timeoutMs: HOME_CONSOLE_TIMEOUT_MS,
        retries: 1,
      });
      if (!isRouteCurrent(routeSeq)) return;
      st.sharePickerDevices = mergeSharePickerDevicePages([], pg.items || []);
      st.sharePickerNextCursor = pg.page && pg.page.next_cursor != null ? String(pg.page.next_cursor) : null;
      st.sharePickerHasMore = !!(pg.page && pg.page.has_more);
      renderShareDevicePicker(false);
    } catch (_) {
      if (!isRouteCurrent(routeSeq)) return;
      st.sharePickerDevices = mergeSharePickerDevicePages([], getDevices());
      st.sharePickerUsingCacheFallback = true;
      st.sharePickerHasMore = false;
      renderShareDevicePicker(false);
    }
  }

  async function appendShareDevicePickerPage() {
    if (!st.sharePickerHasMore || !st.sharePickerNextCursor) return;
    const btn = $("#shareDeviceLoadMore", view);
    if (btn) btn.disabled = true;
    try {
      const pg = await loadShareModalDevicePickerPage({
        live: false,
        cursor: st.sharePickerNextCursor,
        timeoutMs: HOME_CONSOLE_TIMEOUT_MS,
        retries: 1,
      });
      if (!isRouteCurrent(routeSeq)) return;
      st.sharePickerDevices = mergeSharePickerDevicePages(st.sharePickerDevices, pg.items || []);
      st.sharePickerNextCursor = pg.page && pg.page.next_cursor != null ? String(pg.page.next_cursor) : null;
      st.sharePickerHasMore = !!(pg.page && pg.page.has_more);
      renderShareDevicePicker(true);
    } catch (e) {
      toast(String(e && e.message) || "Failed to load more boards", "err");
    } finally {
      if (btn && isRouteCurrent(routeSeq)) btn.disabled = false;
    }
  }

  async function refreshShareItemsSilently() {
    if (!canManageShares()) return;
    try {
      const pack = await fetchAllAdminShares({ timeoutMs: 12000, activeOnly: true });
      st.overviewShareItems = pack.items;
    } catch (_) { /* keep previous cache */ }
  }

  async function loadShareGrants() {
    const wrap = $("#shareGrantsTableWrap", view);
    if (!wrap || !canManageShares()) return;
    const statLine = $("#grpShareStatLine", view);
    const setShareStat = (t) => {
      if (statLine) statLine.textContent = t || "";
    };
    setShareStat("");
    setChildMarkup(wrap, `<p class="muted" style="margin:0;padding:8px 0">Loading…</p>`);
    try {
      const pack = await fetchAllAdminShares({ timeoutMs: 22000, activeOnly: true });
      st.overviewShareItems = pack.items;
      const rows = sortOverviewShareGrantRows(st.overviewShareItems);
      if (!rows.length) {
        setShareStat("No active shares — group summary is empty.");
        setChildMarkup(wrap, `<p class="muted" style="margin:0;padding:8px 0">No active shares in your scope.</p>`);
        return;
      }
      setShareStat(buildOverviewShareGrantsStatLine(rows, getDevices(), canonicalGroupKey));
      setChildMarkup(wrap, buildOverviewShareGrantsTableHtml(rows, escapeHtml));
    } catch (e) {
      setShareStat("");
      setChildMarkup(wrap, `<p class="badge revoked">${escapeHtml(e.message || e)}</p>`);
    }
  }

  function renderShareUserPickList() {
    const userListEl = $("#shareUserList", view);
    if (!userListEl) return;
    const preserve = new Map();
    $$("input[type='checkbox']", userListEl).forEach((inp) => {
      const v = String(inp.value || "").trim();
      if (!inp.disabled && v) preserve.set(v, !!inp.checked);
    });
    if (st.shareModalGroupMode && st.shareModalGroupInitialGrantees instanceof Set) {
      for (const x of st.shareModalUsersCache) {
        const u = String(x.username || "").trim();
        if (u && st.shareModalGroupInitialGrantees.has(u)) preserve.set(u, true);
      }
    }
    if (!st.shareModalUsersCache.length) {
      setChildMarkup(userListEl, `<p class="muted">No eligible users.</p>`);
      return;
    }
    const selIds = $$("#shareDeviceList input[type='checkbox']", view)
      .filter((x) => x.checked && !x.disabled)
      .map((x) => String(x.value || "").trim())
      .filter(Boolean);
    const locked = granteesFullyCoveringDevices(selIds, st.overviewShareItems);
    const eg = st.shareModalEditSpec;
    const editUser = eg ? String(eg.grantee_username || "").trim() : "";
    setChildMarkup(
      userListEl,
      st.shareModalUsersCache.map((x) => {
        const u = String(x.username || "").trim();
        const uname = escapeHtml(u);
        const role = escapeHtml(x.role || "user");
        const em = String(x.email || "").trim();
        const emailBit = em ? ` <span class="mono muted">${escapeHtml(em)}</span>` : "";
        const isEditUser = !!(eg && editUser && u === editUser);
        const isLocked = !isEditUser && selIds.length > 0 && locked.has(u);
        const checked = isEditUser ? true : !!preserve.get(u);
        const dis = isLocked || isEditUser ? "disabled" : "";
        const lockNote = isLocked ? ` <span class="muted" title="Already has access on every selected board">(already shared)</span>` : "";
        const cls = isLocked ? "grp-pick-item is-grant-locked" : "grp-pick-item";
        return `<label class="${cls}"><input type="checkbox" value="${uname}" ${checked ? "checked" : ""} ${dis}/> <span>${uname} <span class="mono">(${role})</span>${emailBit}${lockNote}</span></label>`;
      }).join("") || `<p class="muted">No active admin/user accounts.</p>`,
    );
  }

  function setGroupGranteesLoadUi(ok, errMsg) {
    st.shareModalGroupGranteesLoadOk = ok;
    const applyBtn = $("#shareModalApply", view);
    const retryBtn = $("#shareModalRetryGrantees", view);
    const statEl = $("#shareBatchStat", view);
    if (applyBtn && st.shareModalGroupMode) applyBtn.disabled = !ok;
    if (retryBtn) retryBtn.style.display = (st.shareModalGroupMode && !ok) ? "" : "none";
    if (statEl && st.shareModalGroupMode) {
      statEl.innerHTML = ok
        ? ""
        : `<p class="badge revoked" style="margin:0">${escapeHtml(errMsg || "Could not load who already has access — Apply stays off.")}</p>`;
    }
  }

  async function loadGroupGranteesForModal() {
    const me = getMe();
    if (
      !st.shareModalGroupMode
      || !st.sharePrefillGroup
      || (me && me.role === "superadmin" && !String(st.sharePrefillOwner || "").trim())
    ) {
      setGroupGranteesLoadUi(true, "");
      return;
    }
    setGroupGranteesLoadUi(true, "");
    st.shareModalGroupInitialGrantees = new Set();
    try {
      const ownerArg = me && me.role === "superadmin" && st.sharePrefillOwner
        ? String(st.sharePrefillOwner).trim()
        : undefined;
      const active = await fetchGroupShareGrantees({
        group_display: st.sharePrefillGroup,
        owner_admin: ownerArg,
        timeoutMs: 16000,
      });
      if (!isRouteCurrent(routeSeq)) {
        closeShareModal();
        return;
      }
      for (const it of active) {
        const g = String(it.grantee_username || "").trim();
        if (g) st.shareModalGroupInitialGrantees.add(g);
      }
      const perms = granteePermissionSummary(active);
      const pv = $("#sharePermView", view);
      const po = $("#sharePermOperate", view);
      if (pv) pv.checked = perms.can_view;
      if (po) po.checked = perms.can_operate;
      setGroupGranteesLoadUi(true, "");
    } catch (e) {
      if (!isRouteCurrent(routeSeq)) {
        closeShareModal();
        return;
      }
      st.shareModalGroupInitialGrantees = new Set();
      setGroupGranteesLoadUi(false, formatGroupShareApplyError(e));
      toast(
        isGroupShareDbBusyError(e)
          ? "Could not load access list — system busy. Retry in a few seconds."
          : "Could not load who already has access — Apply stays off until retry works.",
        "warn",
      );
    }
    renderShareUserPickList();
  }

  function closeShareModal() {
    if (shareModalEl) {
      shareModalEl.style.display = "none";
      shareModalEl.classList.remove("is-open");
      shareModalEl.setAttribute("aria-hidden", "true");
      shareModalEl.hidden = true;
    }
    st.shareModalEditSpec = null;
    st.shareModalGroupMode = false;
    st.shareModalGroupInitialGrantees = null;
    st.shareModalGroupGranteesLoadOk = true;
    st.sharePickerDevices = [];
    st.sharePickerNextCursor = null;
    st.sharePickerHasMore = false;
    st.sharePickerUsingCacheFallback = false;
    const loadWrap = $("#shareDeviceLoadMoreWrap", view);
    if (loadWrap) loadWrap.style.display = "none";
    const applyBtn = $("#shareModalApply", view);
    if (applyBtn) applyBtn.disabled = false;
    const retryBtn = $("#shareModalRetryGrantees", view);
    if (retryBtn) retryBtn.style.display = "none";
    const allDev = $("#shareSelAllDevices", view);
    if (allDev) allDev.disabled = false;
    const devListTitle = $("#shareDeviceListTitle", view);
    const devSelAllLbl = $("#shareSelAllDevicesLabel", view);
    if (devListTitle) devListTitle.textContent = "Boards";
    if (devSelAllLbl) devSelAllLbl.style.display = "";
    const titleEl = $("#shareModalTitle", view);
    if (titleEl) titleEl.textContent = "Share boards / group";
    const noteEl = $("#shareEditNote", view);
    if (noteEl) { noteEl.style.display = "none"; noteEl.textContent = ""; }
  }

  async function openShareModal(prefillGroup, prefillOwner, editSpec) {
    if (!shareModalEl) return;
    const me = getMe();
    st.shareModalEditSpec = editSpec && typeof editSpec === "object" ? editSpec : null;
    const editDid = st.shareModalEditSpec ? String(st.shareModalEditSpec.device_id || "").trim() : "";
    st.sharePrefillGroup = String(prefillGroup || "").trim();
    st.sharePrefillOwner = String(prefillOwner || "").trim();
    st.shareModalGroupMode = !!(st.sharePrefillGroup && !st.shareModalEditSpec);
    if (st.shareModalGroupMode && me && me.role === "superadmin" && !st.sharePrefillOwner) {
      st.sharePrefillOwner = inferUniqueOwnerForGroup(st.sharePrefillGroup);
    }
    const devListEl = $("#shareDeviceList", view);
    const userListEl = $("#shareUserList", view);
    const hintEl = $("#shareTargetHint", view);
    const statEl = $("#shareBatchStat", view);
    const titleEl = $("#shareModalTitle", view);
    const noteEl = $("#shareEditNote", view);
    const devListTitle = $("#shareDeviceListTitle", view);
    const devSelAllLbl = $("#shareSelAllDevicesLabel", view);
    if (!devListEl || !userListEl || !hintEl || !statEl) return;
    statEl.textContent = "";
    if (titleEl) {
      titleEl.textContent = st.shareModalEditSpec
        ? "Edit board share"
        : (st.shareModalGroupMode ? "Share notification group" : "Share boards / group");
    }
    if (noteEl) {
      if (st.shareModalEditSpec) {
        noteEl.style.display = "block";
        noteEl.textContent = `Board ${editDid} · shared with ${String(st.shareModalEditSpec.grantee_username || "")} — adjust permissions and apply.`;
      } else {
        noteEl.style.display = "none";
        noteEl.textContent = "";
      }
    }
    if (st.shareModalEditSpec) {
      hintEl.textContent = "Permissions apply to this board and user. Group ownership stays with the account owner.";
      const pv = $("#sharePermView", view);
      const po = $("#sharePermOperate", view);
      if (pv) pv.checked = !!st.shareModalEditSpec.can_view;
      if (po) po.checked = !!st.shareModalEditSpec.can_operate;
    } else if (st.shareModalGroupMode) {
      hintEl.textContent = `Notification group “${st.sharePrefillGroup}” — boards below are read-only. Users already shared are pre-checked; uncheck someone to revoke this group only for them (all boards in this group). Then Apply.`;
      const pv = $("#sharePermView", view);
      const po = $("#sharePermOperate", view);
      if (pv) pv.checked = true;
      if (po) po.checked = false;
    } else {
      hintEl.textContent = "Select own boards and users. Grants are per board; use Load more if the fleet is large. Users already fully covered on the current selection are locked — use Edit in the table.";
      const pv = $("#sharePermView", view);
      const po = $("#sharePermOperate", view);
      if (pv) pv.checked = true;
      if (po) po.checked = false;
    }
    const devices = getDevices();
    if (devListTitle) devListTitle.textContent = st.shareModalGroupMode ? "Boards in group" : "Boards";
    if (devSelAllLbl) devSelAllLbl.style.display = st.shareModalGroupMode ? "none" : "";
    const loadWrapEarly = $("#shareDeviceLoadMoreWrap", view);
    if (loadWrapEarly) loadWrapEarly.style.display = "none";
    if (st.shareModalEditSpec) {
      const row = devices.find((d) => String(d.device_id) === editDid);
      setChildMarkup(
        devListEl,
        row
          ? groupDeviceRow(row, { checked: true, disabled: true })
          : `<label class="grp-pick-item grp-pick-item--device">` +
            `<input type="checkbox" class="grp-pick-chk" value="${escapeHtml(editDid)}" checked disabled />` +
            `<span class="grp-pick-text">` +
            `<span class="grp-pick-name">${escapeHtml(editDid)}</span>` +
            `<span class="grp-pick-id mono">${escapeHtml(editDid)}</span></span></label>`,
      );
    } else if (st.shareModalGroupMode) {
      setChildMarkup(devListEl, `<p class="muted">Loading group boards…</p>`);
      if (me && me.role === "superadmin" && !String(st.sharePrefillOwner || "").trim()) {
        setChildMarkup(
          devListEl,
          `<p class="badge revoked">Cannot load preview: pick a group that has a clear account owner, or open share from that owner’s group card.</p>`,
        );
      } else try {
        const pr = await api(buildAdminNotificationGroupSharePreviewPath({
          group_display: st.sharePrefillGroup,
          owner_admin: me && me.role === "superadmin" && st.sharePrefillOwner ? st.sharePrefillOwner : undefined,
        }), { timeoutMs: HOME_CONSOLE_TIMEOUT_MS });
        if (!isRouteCurrent(routeSeq)) {
          closeShareModal();
          return;
        }
        const chips = (pr.items || []).map((it) => {
          const nm = String((it.display_label_short != null && it.display_label_short !== "") ? it.display_label_short : (it.display_label || it.device_id || "")).trim();
          const tail = String(it.device_id_tail || "").trim();
          const line = tail ? `${nm} · …${tail}` : nm;
          return `<span class="chip" style="margin:2px">${escapeHtml(line)}</span>`;
        }).join("");
        const cnt = Number(pr.device_count != null ? pr.device_count : (pr.items || []).length);
        setChildMarkup(
          devListEl,
          `<div style="display:flex;flex-wrap:wrap;gap:4px;align-items:center">${chips || "<span class=\"muted\">No boards in this group.</span>"}</div>` +
            `<p class="muted" style="margin:8px 0 0">${cnt} board(s) will receive shared access.</p>`,
        );
      } catch (e) {
        if (!isRouteCurrent(routeSeq)) {
          closeShareModal();
          return;
        }
        setChildMarkup(devListEl, `<p class="badge revoked">${escapeHtml(e.message || e)}</p>`);
      }
    } else {
      await loadShareDevicePickerInitial();
    }
    if (!st.shareDevChangeBound) {
      st.shareDevChangeBound = true;
      devListEl.addEventListener("change", () => {
        if (shareModalEl && shareModalEl.style.display === "flex" && !st.shareModalEditSpec) renderShareUserPickList();
      });
    }
    await refreshShareItemsSilently();
    st.shareModalGroupInitialGrantees = null;
    setChildMarkup(userListEl, `<p class="muted">Loading users…</p>`);
    try {
      const u = await api(buildAuthUsersPath(), { timeoutMs: ADMIN_CONSOLE_TIMEOUT_MS });
      if (!isRouteCurrent(routeSeq)) {
        closeShareModal();
        return;
      }
      st.shareModalUsersCache = (u.items || []).filter((x) => {
        const role = String(x.role || "");
        const st0 = String(x.status || "active");
        if (!(st0 === "active" || st0 === "")) return false;
        if (me && me.role === "admin") return role === "user";
        return role === "admin" || role === "user";
      });
      if (
        st.shareModalGroupMode
        && st.sharePrefillGroup
        && !(me && me.role === "superadmin" && !String(st.sharePrefillOwner || "").trim())
      ) {
        await loadGroupGranteesForModal();
      } else {
        st.shareModalGroupInitialGrantees = null;
        setGroupGranteesLoadUi(true, "");
        renderShareUserPickList();
      }
    } catch (e) {
      if (!isRouteCurrent(routeSeq)) {
        closeShareModal();
        return;
      }
      st.shareModalUsersCache = [];
      setChildMarkup(userListEl, `<p class="badge revoked">${escapeHtml(e.message || e)}</p>`);
    }
    if (!isRouteCurrent(routeSeq)) {
      closeShareModal();
      return;
    }
    const allDev = $("#shareSelAllDevices", view);
    const allUsr = $("#shareSelAllUsers", view);
    if (allDev) {
      allDev.checked = false;
      allDev.disabled = !!st.shareModalEditSpec || st.shareModalGroupMode;
      allDev.onchange = () => {
        $$("#shareDeviceList input[type='checkbox']:not([disabled])", view).forEach((x) => { x.checked = !!allDev.checked; });
        if (!st.shareModalEditSpec) renderShareUserPickList();
      };
    }
    if (allUsr) {
      allUsr.checked = false;
      allUsr.onchange = () => {
        $$("#shareUserList input[type='checkbox']:not([disabled])", view).forEach((x) => { x.checked = !!allUsr.checked; });
      };
    }
    shareModalEl.hidden = false;
    shareModalEl.removeAttribute("aria-hidden");
    shareModalEl.classList.add("is-open");
    shareModalEl.style.display = "flex";
  }

  async function applyShareModal() {
    const me = getMe();
    const statEl = $("#shareBatchStat", view);
    const usernames = $$("#shareUserList input[type='checkbox']", view).filter((x) => x.checked).map((x) => String(x.value || "").trim()).filter(Boolean);
    const canView = !!$("#sharePermView", view)?.checked;
    const canOperate = !!$("#sharePermOperate", view)?.checked;
    if (!st.shareModalGroupMode) {
      if (!usernames.length) { toast("Select at least one user", "err"); return; }
      if (!canView && !canOperate) { toast("Select at least one permission", "err"); return; }
    }
    if (st.shareModalGroupMode) {
      if (!st.shareModalGroupGranteesLoadOk) {
        toast("Cannot apply — member list unknown. Use Retry loading members.", "err");
        return;
      }
      const gd = String(st.sharePrefillGroup || "").trim();
      if (!gd) { toast("Missing group", "err"); return; }
      if (me && me.role === "superadmin" && !String(st.sharePrefillOwner || "").trim()) {
        toast("Cannot share this group yet: open Share from a group card that shows an account owner.", "err");
        return;
      }
      const initial = st.shareModalGroupInitialGrantees instanceof Set ? st.shareModalGroupInitialGrantees : new Set();
      const checkedNow = new Set(usernames);
      if (checkedNow.size === 0 && initial.size === 0) {
        toast("No changes — check users to share or uncheck to revoke this group.", "warn");
        return;
      }
      if (checkedNow.size > 0 && !canView && !canOperate) {
        toast("Select at least one permission for users who remain shared.", "err");
        return;
      }
      const ownerTrim = String(st.sharePrefillOwner || "").trim();
      const grantees = [...checkedNow].map((u) => ({
        grantee_username: u,
        can_view: canView,
        can_operate: canOperate,
      }));
      if (statEl) statEl.textContent = `Updating group share (${grantees.length} people)…`;
      try {
        const res = await syncGroupShareGrantees({
          group_display: gd,
          owner_admin: me && me.role === "superadmin" && ownerTrim ? ownerTrim : undefined,
          grantees,
          timeoutMs: 45000,
        });
        const rev = Number(res.revoked_count || 0);
        const up = Number(res.upserted_count || 0);
        toast(
          `Group share updated · removed ${rev}, shared ${up}. Shared users can open Groups → Shared with me in a few seconds.`,
          "ok",
        );
        closeShareModal();
        loadShareGrants();
        try { bustDeviceListCaches(); } catch (_) {}
      } catch (e) {
        const msg = formatGroupShareApplyError(e);
        const busy = isGroupShareDbBusyError(e);
        if (statEl) statEl.textContent = busy ? `${msg} (Apply not saved.)` : msg;
        toast(msg, busy ? "warn" : "err");
        if (!busy) {
          loadShareGrants();
          try { bustDeviceListCaches(); } catch (_) {}
        }
      }
      return;
    }
    const deviceIds = $$("#shareDeviceList input[type='checkbox']", view).filter((x) => x.checked).map((x) => String(x.value || "").trim()).filter(Boolean);
    if (!deviceIds.length) { toast("Select at least one board", "err"); return; }
    const total = deviceIds.length * usernames.length;
    if (statEl) statEl.textContent = `Applying ${total} share grants…`;
    const res = await grantShareMatrix(
      deviceIds,
      usernames,
      { can_view: canView, can_operate: canOperate },
      (p) => {
        if (!statEl) return;
        const err = p.lastError ? ` · last: ${p.lastError}` : "";
        statEl.textContent = `Applied ${p.idx}/${p.total} · ok ${p.ok} · failed ${p.fail}${err}`;
      },
    );
    const ok = Number(res.ok || 0);
    const fail = Number(res.fail || 0);
    if (fail === 0) {
      toast(`Sharing applied (${ok}/${total})`, "ok");
      closeShareModal();
      loadShareGrants();
      try { bustDeviceListCaches(); } catch (_) {}
    } else {
      toast(`Sharing done with failures (${ok} ok, ${fail} failed)`, "warn");
      loadShareGrants();
      try { bustDeviceListCaches(); } catch (_) {}
    }
  }

  function wireHandlers() {
    const grpShareOpenBtn = $("#grpShareOpen", view);
    if (grpShareOpenBtn) grpShareOpenBtn.addEventListener("click", () => openShareModal("", "", null));
    const grpShareRefreshBtn = $("#grpShareRefresh", view);
    if (grpShareRefreshBtn) grpShareRefreshBtn.addEventListener("click", () => loadShareGrants());
    const shareGrantsWrap = $("#shareGrantsTableWrap", view);
    if (shareGrantsWrap) {
      shareGrantsWrap.addEventListener("click", async (ev) => {
        const btn = ev.target.closest("button");
        if (!btn) return;
        const tr = btn.closest("tr");
        if (!tr) return;
        const device_id = tr.getAttribute("data-device-id") || "";
        const grantee_username = tr.getAttribute("data-grantee") || "";
        if (!device_id || !grantee_username) return;
        const row = st.overviewShareItems.find((x) => String(x.device_id) === device_id && String(x.grantee_username) === grantee_username);
        if (btn.classList.contains("js-share-grant-edit")) {
          openShareModal("", "", row ? {
            device_id: row.device_id,
            grantee_username: row.grantee_username,
            can_view: !!Number(row.can_view),
            can_operate: !!Number(row.can_operate),
          } : {
            device_id,
            grantee_username,
            can_view: true,
            can_operate: false,
          });
          return;
        }
        if (btn.classList.contains("js-share-grant-revoke")) {
          if (!confirm(`Revoke share for ${grantee_username} on ${device_id}?`)) return;
          try {
            await api(buildAdminDeviceShareRevokePath(device_id, grantee_username), { method: "DELETE" });
            toast("Share revoked", "ok");
            await loadShareGrants();
            await refreshShareItemsSilently();
            try { bustDeviceListCaches(); } catch (_) {}
          } catch (e) { toast(e.message || e, "err"); }
        }
      });
    }
    const shareCancelBtn = $("#shareModalCancel", view);
    if (shareCancelBtn) shareCancelBtn.addEventListener("click", closeShareModal);
    const shareRetryBtn = $("#shareModalRetryGrantees", view);
    if (shareRetryBtn) {
      shareRetryBtn.addEventListener("click", async () => {
        const userListEl = $("#shareUserList", view);
        if (userListEl) setChildMarkup(userListEl, `<p class="muted">Retrying member list…</p>`);
        await loadGroupGranteesForModal();
      });
    }
    const shareApplyBtn = $("#shareModalApply", view);
    if (shareApplyBtn) shareApplyBtn.addEventListener("click", () => applyShareModal());
    view.addEventListener("click", (ev) => {
      if (!ev.target || !ev.target.closest) return;
      if (ev.target.closest("#shareDeviceLoadMore")) {
        ev.preventDefault();
        appendShareDevicePickerPage();
      }
    });
  }

  return {
    loadShareGrants,
    refreshShareItemsSilently,
    openShareModal,
    closeShareModal,
    wireHandlers,
  };
}
